import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import ApplicationInitializer from './Infrastructure/ApplicationInitializer';
import { store } from './Infrastructure/store';
import { ErrorBoundary } from 'react-error-boundary';
import FallbackError from './Infrastructure/Components/ErrorHandling/FallbackError';
import { AppSettings } from './Infrastructure/Configuration/types';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { initializeMsalInstance } from './Infrastructure/Security/msalInstance';
import { createAppInsightsInstance } from './Infrastructure/AppInsights/createAppInsightsInstance';
import AppThemeProvider from './Infrastructure/Theme/AppThemeProvider';
import { LicenseInfo } from '@mui/x-license';

import '@fontsource/roboto';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400-italic.css';

// Key is sent to the client anyways so we are not concerned with putting this in appsettings
const MUI_LICENSE_KEY = '1794782e8350fb3b90631e823e2fd7d0Tz0xMjcyNzUsRT0xODA1NzU5OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MSxLVj0y';

/**
 * Get the app settings here because we need them for instantiating the msal & app insight instances.
 * Then pass them into the ApplicationInitializer to be stored in state.
 */
fetch(`/${import.meta.env.TRINIUM_WEB_APPSETTINGS_FILENAME}`, {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }
})
  .then((response) => {
    return response.json();
  })
  .then(async (appSettings: AppSettings) => {
    const reactPlugin = new ReactPlugin();

    await initializeMsalInstance(appSettings);

    const appInsights = createAppInsightsInstance(appSettings, reactPlugin);

    LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

    const rootEl = document.getElementById('root');
    if (rootEl) {
      ReactDOM.createRoot(rootEl).render(
        <React.StrictMode>
          <ErrorBoundary FallbackComponent={FallbackError}>
            <Provider store={store}>
              <AppThemeProvider appSettings={appSettings}>
                <ApplicationInitializer
                  appSettings={appSettings}
                  appInsightsInstance={appInsights}
                  reactPlugin={reactPlugin}
                />
              </AppThemeProvider>
            </Provider>
          </ErrorBoundary>
        </React.StrictMode>
      );
    } else {
      throw new Error('Root Element could not be found.');
    }
  })
  .catch((ex: unknown) => {
    console.log(ex);
  });
